import type { FlowEdge, FlowNode } from "@hali-shares/deck-schema";
import { useState } from "react";
import { Check, RefsPicker, Select, TextArea, TextField, UpIcon, XIcon, move } from "../fields";
import { chainEdges, inOrderEdges, inOrderShape, rewireAfterMove, slugify, uniqueId } from "../model";
import type { B, EditorProps } from "./types";

type SubFlow = NonNullable<NonNullable<FlowNode["detail"]>["flow"]>;

/** Sub-flow as "Label | caption" lines, joined in order, optionally looping back from the last step. */
function subToText(sub: SubFlow | undefined): string {
  return (sub?.nodes ?? []).map((n) => (n.caption ? `${n.label} | ${n.caption}` : n.label)).join("\n");
}

function textToSub(text: string, loop: string | null, emphasisLabel: string | undefined): SubFlow | undefined {
  const taken: string[] = [];
  const nodes = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const [label = "", caption] = line.split("|").map((s) => s.trim());
      const id = uniqueId(slugify(label, 20) || "step", taken);
      taken.push(id);
      return { id, label, ...(caption && { caption }) };
    });
  if (nodes.length < 2) return undefined;
  const edges: FlowEdge[] = chainEdges(nodes.map((n) => n.id));
  if (loop !== null)
    edges.push({ from: nodes[nodes.length - 1]!.id, to: nodes[0]!.id, kind: "loop", ...(loop && { label: loop }) });
  const emphasis = nodes.find((n) => n.label === emphasisLabel)?.id;
  return { nodes, edges, ...(emphasis && { emphasis }) };
}

function SubFlowField({ value, onChange }: { value: SubFlow | undefined; onChange: (v: SubFlow | undefined) => void }) {
  const loopEdge = value?.edges.find((e) => e.kind === "loop");
  const [text, setText] = useState(subToText(value));
  const [loop, setLoop] = useState<string | null>(loopEdge ? (loopEdge.label ?? "") : null);
  const [emphasis, setEmphasis] = useState(value?.nodes.find((n) => n.id === value.emphasis)?.label);
  const labels = text
    .split("\n")
    .map((l) => l.split("|")[0]!.trim())
    .filter(Boolean);
  const apply = (t = text, l = loop, e = emphasis) => onChange(textToSub(t, l, e));
  return (
    <>
      <TextArea
        label="Small flow inside (optional)"
        hint="2 to 5 steps, one per line, as “Label | caption”. They’re joined in order."
        mono
        value={text}
        onChange={(t) => {
          setText(t);
          apply(t);
        }}
      />
      {labels.length >= 2 && (
        <div className="grid-2">
          <Select
            label="Yellow step"
            value={emphasis ?? ""}
            options={[{ value: "", label: "None" }, ...labels.map((l) => ({ value: l, label: l }))]}
            onChange={(e) => {
              setEmphasis(e || undefined);
              apply(text, loop, e || undefined);
            }}
          />
          <div className="field">
            <Check
              label="Loop back to the first step"
              checked={loop !== null}
              onChange={(on) => {
                setLoop(on ? "" : null);
                apply(text, on ? "" : null);
              }}
            />
            {loop !== null && (
              <input
                className="input"
                aria-label="Loop label"
                placeholder="Loop label, e.g. next step"
                value={loop}
                maxLength={40}
                onChange={(e) => {
                  setLoop(e.target.value);
                  apply(text, e.target.value);
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function FlowEditor({ value: b, onChange, deck }: EditorProps<B<"flow">>) {
  const set = (patch: Partial<B<"flow">>) => onChange({ ...b, ...patch });
  const ids = b.nodes.map((n) => n.id);
  const shape = inOrderShape(ids, b.edges);
  // "Custom" sticks once chosen, even if the arrows happen to form a plain chain again.
  const [custom, setCustom] = useState(shape === null);
  const loopLabel = shape?.loopLabel ?? b.loopLabel ?? "";

  const setNode = (i: number, patch: Partial<FlowNode>) =>
    set({ nodes: b.nodes.map((n, j) => (j === i ? { ...n, ...patch } : n)) });
  /** New steps or order: in-order flows re-join themselves; custom arrows keep pointing at the same steps. */
  const setNodes = (nodes: FlowNode[], edges: FlowEdge[]) => {
    const next = nodes.map((n) => n.id);
    set({ nodes, edges: custom ? edges : inOrderEdges(next, shape?.loop ?? false) });
  };
  const removeNode = (i: number) => {
    const gone = b.nodes[i]!.id;
    setNodes(
      b.nodes.filter((_, j) => j !== i),
      b.edges.filter((e) => e.from !== gone && e.to !== gone),
    );
  };
  const moveNode = (from: number, to: number) => {
    const nodes = move(b.nodes, from, to);
    setNodes(
      nodes,
      rewireAfterMove(
        ids,
        nodes.map((n) => n.id),
        b.edges,
      ) ?? b.edges,
    );
  };
  const addNode = () => {
    const id = uniqueId("step", ids);
    const last = ids[ids.length - 1]!;
    setNodes(
      [...b.nodes, { id, label: `Step ${b.nodes.length + 1}` }],
      [...b.edges, { from: last, to: id, kind: "forward" }],
    );
  };
  const setEdge = (i: number, patch: Partial<FlowEdge>) =>
    set({ edges: b.edges.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  const zoomCount = b.nodes.filter((n) => n.detail).length;
  const name = (id: string) => {
    const i = ids.indexOf(id);
    return `${i + 1}. ${b.nodes[i]?.label || "Untitled"}`;
  };

  return (
    <>
      <TextField label="Title" max={80} value={b.title} onChange={(title) => set({ title })} />
      <TextArea label="Lead" max={240} value={b.lead} onChange={(lead) => set({ lead: lead || undefined })} />

      <div className="field">
        <div className="field-label">
          <span>Steps</span>
          <span className="field-count">{b.nodes.length}/7</span>
        </div>
        <ol className="flow-steps">
          {b.nodes.map((n, i) => (
            <li key={n.id} className="flow-step">
              <div className="flow-step-row">
                <span className="flow-step-num">{i + 1}</span>
                <input
                  className="input"
                  aria-label={`Step ${i + 1} label`}
                  placeholder="Label"
                  maxLength={24}
                  value={n.label}
                  onChange={(e) => setNode(i, { label: e.target.value })}
                />
                <input
                  className="input"
                  aria-label={`Step ${i + 1} caption`}
                  placeholder="Caption (optional)"
                  maxLength={48}
                  value={n.caption ?? ""}
                  onChange={(e) => setNode(i, { caption: e.target.value || undefined })}
                />
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Move step ${i + 1} up`}
                  disabled={i === 0}
                  onClick={() => moveNode(i, i - 1)}
                >
                  <UpIcon />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Move step ${i + 1} down`}
                  disabled={i === b.nodes.length - 1}
                  onClick={() => moveNode(i, i + 1)}
                >
                  <UpIcon down />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Remove step ${i + 1}`}
                  disabled={b.nodes.length <= 2}
                  onClick={() => removeNode(i)}
                >
                  <XIcon />
                </button>
              </div>
              <Check
                label="Zoom stop: the presenter can zoom into this step"
                checked={Boolean(n.detail)}
                onChange={(on) => setNode(i, { detail: on ? { title: n.label, refs: [] } : undefined })}
              />
              {n.detail && (
                <div className="flow-step-detail">
                  <TextField
                    label="Zoomed title"
                    max={80}
                    value={n.detail.title}
                    onChange={(title) => setNode(i, { detail: { ...n.detail!, title } })}
                  />
                  <TextArea
                    label="Zoomed lead"
                    max={240}
                    value={n.detail.lead}
                    onChange={(lead) => setNode(i, { detail: { ...n.detail!, lead: lead || undefined } })}
                  />
                  <SubFlowField
                    value={n.detail.flow}
                    onChange={(flow) => setNode(i, { detail: { ...n.detail!, flow } })}
                  />
                  <TextField
                    label="Code example (one line)"
                    hint="A function call like name({ ... }) shows the function name in yellow."
                    mono
                    max={300}
                    value={n.detail.code}
                    onChange={(code) => setNode(i, { detail: { ...n.detail!, code: code || undefined } })}
                  />
                  <RefsPicker
                    label="Footnotes when zoomed"
                    references={deck.references}
                    value={n.detail.refs}
                    onChange={(refs) => setNode(i, { detail: { ...n.detail!, refs } })}
                  />
                </div>
              )}
            </li>
          ))}
        </ol>
        {b.nodes.length >= 7 ? (
          <p className="field-hint">
            7 of 7 steps: a flow can’t show more. To add one, merge two neighbours (e.g. “Staging + smoke”).
          </p>
        ) : (
          <button type="button" className="s-btn s-btn-dashed" style={{ alignSelf: "flex-start" }} onClick={addNode}>
            + Step
          </button>
        )}
        {zoomCount * 3 > b.nodes.length + 2 && (
          <p className="field-hint">Tip: keep zoom stops to about one in three steps.</p>
        )}
      </div>

      <fieldset className="field flow-links">
        <legend className="field-label">Arrows</legend>
        <label className="check">
          <input
            type="radio"
            name={`links-${b.id}`}
            checked={!custom}
            onChange={() => {
              setCustom(false);
              set({ edges: inOrderEdges(ids, shape?.loop ?? b.edges.some((e) => e.kind === "loop")) });
            }}
          />
          In order: {b.nodes.map((n) => n.label || "…").join(" → ")}
        </label>
        <label className="check">
          <input type="radio" name={`links-${b.id}`} checked={custom} onChange={() => setCustom(true)} />
          Custom: branches, skips, several loops
        </label>
        {!custom ? (
          <div className="flow-loop">
            <Check
              label={`Loop back from “${b.nodes[b.nodes.length - 1]?.label}” to “${b.nodes[0]?.label}”`}
              checked={shape?.loop ?? false}
              onChange={(on) => set({ edges: inOrderEdges(ids, on) })}
            />
            {shape?.loop && (
              <input
                className="input"
                aria-label="Loop label"
                placeholder="Label under the loop, e.g. until the goal is met"
                maxLength={40}
                value={loopLabel}
                onChange={(e) => set({ edges: inOrderEdges(ids, true), loopLabel: e.target.value || undefined })}
              />
            )}
          </div>
        ) : (
          <>
            <ul className="fe-arrows">
              {b.edges.map((e, i) => (
                <li key={i} className="fe-arrow">
                  <select
                    className="select"
                    aria-label={`Arrow ${i + 1} from`}
                    value={e.from}
                    onChange={(ev) => setEdge(i, { from: ev.target.value })}
                  >
                    {ids.map((id) => (
                      <option key={id} value={id}>
                        {name(id)}
                      </option>
                    ))}
                  </select>
                  <span aria-hidden="true" className="fe-arrow-glyph">
                    →
                  </span>
                  <select
                    className="select"
                    aria-label={`Arrow ${i + 1} to`}
                    value={e.to}
                    onChange={(ev) => setEdge(i, { to: ev.target.value })}
                  >
                    {ids.map((id) => (
                      <option key={id} value={id}>
                        {name(id)}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input"
                    aria-label={`Arrow ${i + 1} label`}
                    placeholder="Label, e.g. yes"
                    maxLength={40}
                    value={e.label ?? ""}
                    onChange={(ev) => setEdge(i, { label: ev.target.value || undefined })}
                  />
                  <Check
                    label="Loop"
                    checked={e.kind === "loop"}
                    onChange={(on) => setEdge(i, { kind: on ? "loop" : "forward" })}
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Remove arrow ${i + 1}`}
                    onClick={() => set({ edges: b.edges.filter((_, j) => j !== i) })}
                  >
                    <XIcon />
                  </button>
                </li>
              ))}
            </ul>
            {b.edges.length < 12 && (
              <button
                type="button"
                className="s-btn s-btn-dashed"
                style={{ alignSelf: "flex-start" }}
                onClick={() =>
                  set({ edges: [...b.edges, { from: ids[0]!, to: ids[ids.length - 1]!, kind: "forward" }] })
                }
              >
                + Arrow
              </button>
            )}
            <span className="field-hint">
              Loops are drawn dashed, below the steps. An arrow back to an earlier step should be a loop.
            </span>
          </>
        )}
      </fieldset>
      <RefsPicker references={deck.references} value={b.refs} onChange={(refs) => set({ refs })} />
      <TextArea
        label="Speaker notes"
        max={2000}
        value={b.notes}
        onChange={(notes) => set({ notes: notes || undefined })}
      />
    </>
  );
}
